import { useAuthLoginCreate, useAuthStatusRetrieve, useAuthLogoutCreate } from '@/gen-api/api';
import { useAuthStore, AuthCredentials } from '@/store/authStore';
import { AxiosError } from 'axios';
import { useCallback } from 'react';
import axios from 'axios';

// Import the AuthStatusResponse from generated models
import { AuthStatusResponse } from '@/gen-api/models/authStatusResponse';

// Direct API service for non-hook usage
export const authService = {
  async login(credentials: { email: string; password: string }) {
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/authentication/login/`, credentials);
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      return null;
    }
  },

  async requestPasswordReset(email: string) {
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/authentication/forgot-password/`, { email });
      return response.data;
    } catch (error) {
      console.error('Password reset request failed:', error);
      throw error;
    }
  },
};

export const useAuthService = () => {
  const { signIn, signOut, setUserInfo, setCanCreateWorkspace } = useAuthStore();

  const authStatusQuery = useAuthStatusRetrieve({
    query: {
      enabled: false,
      retry: false,
    },
  });
  const loginMutation = useAuthLoginCreate();
  const logoutMutation = useAuthLogoutCreate();

  const checkAuthStatus = useCallback(async () => {
    const currentCredentials = useAuthStore.getState().credentials;

    if (!currentCredentials?.accessToken) {
      signOut();
      setUserInfo(null);
      setCanCreateWorkspace(false);
      return { isAuthenticated: false, user: null };
    }
    try {
      const { data, error, isError } = await authStatusQuery.refetch();
      // Explicitly cast to handle potential undefined
      const authResponse = data as AuthStatusResponse | undefined;

      // Handle explicit API error first
      if (isError || error) {
        signOut();
        setUserInfo(null);
        setCanCreateWorkspace(false);
        return { isAuthenticated: false, user: null, error };
      }

      // Check if response data is valid and indicates authenticated
      if (authResponse?.authenticated && authResponse.user) {
        // Update user info and workspaces in the store
        setUserInfo(authResponse.user);
        setCanCreateWorkspace(authResponse.can_create_workspace ?? false);

        // Update workspaces in the store if present in response
        if (authResponse.workspaces) {
          useAuthStore.getState().setWorkspaces(authResponse.workspaces);
        }

        // Set current workspace if present in response
        if (authResponse.active_workspace) {
          // Convert active_workspace to WorkspaceMemberResponseSerializerSchema format
          // by finding the matching workspace in the workspaces list
          const matchingWorkspace = authResponse.workspaces?.find((ws) => ws.id === authResponse.active_workspace?.id);

          if (matchingWorkspace) {
            useAuthStore.getState().setCurrentWorkspace(matchingWorkspace);
          }
        }

        return { isAuthenticated: true, user: authResponse.user };
      } else {
        signOut();
        setUserInfo(null);
        setCanCreateWorkspace(false);
        // If authResponse was undefined, create a specific error.
        const returnedError =
          authResponse === undefined
            ? new Error('Auth status response data was undefined')
            : new Error('Invalid auth status response');
        return { isAuthenticated: false, user: null, error: returnedError };
      }
    } catch (catchError) {
      // Catch block for errors during refetch() call itself or subsequent logic
      console.error('(useCallback) Catch block: Error during auth status API check:', catchError);
      signOut();
      setUserInfo(null);
      setCanCreateWorkspace(false);
      return { isAuthenticated: false, user: null, error: catchError };
    }
  }, [authStatusQuery, setUserInfo, signOut, setCanCreateWorkspace]);

  const login = useCallback(
    async (loginData: { email: string; password: string }) => {
      try {
        const response = await loginMutation.mutateAsync({ data: loginData });
        const authData = response;

        const newCredentials: AuthCredentials = {
          accessToken: authData.access,
          refreshToken: authData.refresh,
        };

        signIn(newCredentials, authData.user, authData.workspaces);
        setUserInfo(authData.user);
        setCanCreateWorkspace(authData.can_create_workspace ?? false);

        return { success: true, user: authData.user };
      } catch (error) {
        console.error('Login failed:', error);
        signOut();
        setUserInfo(null);
        setCanCreateWorkspace(false);
        return { success: false, error: error as AxiosError | Error };
      }
    },
    [loginMutation, signIn, setUserInfo, signOut, setCanCreateWorkspace]
  );

  const logout = useCallback(async () => {
    const currentRefreshToken = useAuthStore.getState().credentials?.refreshToken;
    try {
      if (currentRefreshToken) {
        await logoutMutation.mutateAsync({ data: { refresh: currentRefreshToken } });
      } else {
        console.log('No refresh token found in store, skipping API logout call.');
      }
    } catch (error) {
      console.error('API logout failed:', error);
    } finally {
      console.log('Clearing client-side auth state.');
      signOut();
      setUserInfo(null);
      setCanCreateWorkspace(false);
    }
  }, [logoutMutation, setUserInfo, signOut, setCanCreateWorkspace]);

  return {
    checkAuthStatus,
    login,
    logout,
  };
};
